import type { LensIntent } from '~/types/leData';
import type { LensPlanMode } from '~/types/leLens';

export type OpenScopeAnswer = {
  brief: string;
  questionId: string;
};

/**
 * The LensIntent, if any, this turn's `text` should carry. Kept in its own module (rather than
 * inline in LeDataChat.tsx, which pulls in echarts and cannot be imported directly from a Jest
 * test) so it stays independently unit-testable.
 *
 * A chip's (or a controlled draft's) own pending-intent entry wins when its `visibleText` still
 * matches the text actually being sent (an abandoned draft never attaches a stale intent to
 * unrelated text typed afterwards). Otherwise, in create mode's own empty state
 * (`createEmptyState`, `LensCreateEmptyState`) with no session yet, a brief typed straight into
 * the composer gets the same deterministic `create` intent a chip click would, so the two ways of
 * starting a Lens produce exactly the same turn. Next, while a reader question is open
 * (`openScopeAnswer`), typed text answers it rather than starting a free-thread turn. Once none of those apply, typed text goes back to being an ordinary follow-up
 * (`undefined`).
 */
export function resolveLensIntentForTurn(
  text: string,
  pendingLensIntent: { visibleText: string; lensIntent: LensIntent } | null,
  options: {
    createEmptyState: boolean;
    sourceAnchorId?: string;
    hasSession: boolean;
    mode?: LensPlanMode;
    openScopeAnswer?: OpenScopeAnswer;
  },
): LensIntent | undefined {
  if (pendingLensIntent?.visibleText === text) {
    if (
      options.createEmptyState &&
      !options.hasSession &&
      options.sourceAnchorId &&
      pendingLensIntent.lensIntent.kind === 'create'
    ) {
      return { ...pendingLensIntent.lensIntent, sourceAnchorId: options.sourceAnchorId };
    }
    return pendingLensIntent.lensIntent;
  }
  if (options.createEmptyState && !options.hasSession) {
    return {
      brief: text,
      kind: 'create',
      ...(options.mode ? { mode: options.mode } : {}),
      ...(options.sourceAnchorId ? { sourceAnchorId: options.sourceAnchorId } : {}),
    };
  }
  if (options.openScopeAnswer) {
    const { brief, questionId } = options.openScopeAnswer;
    return { kind: 'answer', brief, questionAnswer: { questionId, text } };
  }
  return undefined;
}
